import React, { useState, useRef, useEffect } from "react";
import { ChatCircleText, X, PaperPlaneRight, Camera, Check, Sparkle, ShoppingCart, CaretDown, CaretUp, WarningCircle } from "@phosphor-icons/react";
import { api, formatCurrency } from "../../utils/api";

// Asistente flotante visible mientras el cliente arma su presupuesto (pasos
// 1-4) — para cuando se queda en blanco sobre qué necesita, o quiere agregar
// algo que no ve en el catálogo que está mirando. Reusa el mismo motor de
// interpretación validado offline en Fase D v2 (test_interpretacion_whatsapp.py)
// contra el endpoint /interpretar-pedido.
//
// Regla de seguridad: nunca agrega nada al carrito sin que el cliente toque
// "Sí, agregar" explícitamente — la IA solo PROPONE.
//
// El asistente confirmaba "Agregado a tu presupuesto" pero el cliente no
// tenía forma de ver ESE presupuesto sin salir del chat y navegar el wizard
// a mano (hallazgo real de un cliente probando la app, 2 sep 2026) — por eso
// el resumen del carrito y el aviso de datos faltantes viven acá adentro,
// visibles sin cerrar el chat.
export const AsistenteFlotantePresupuesto = ({ trabajos, calidad, tipoPresupuesto, lineas, cliente, onAgregarLinea }) => {
  const [abierto, setAbierto] = useState(false);
  const [carritoAbierto, setCarritoAbierto] = useState(false);
  const [mensajes, setMensajes] = useState([
    { role: "assistant", content: "¿En qué te puedo ayudar? Contame qué necesitás (podés mandar una foto también) y te digo si lo tenemos en el catálogo." },
  ]);
  const [input, setInput] = useState("");
  const [imagenAdjunta, setImagenAdjunta] = useState(null);
  const [loading, setLoading] = useState(false);
  const finRef = useRef(null);

  useEffect(() => {
    if (abierto) finRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensajes, abierto]);

  const adjuntarFoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setImagenAdjunta(reader.result);
    reader.readAsDataURL(file);
  };

  const construirLinea = (trabajo) => {
    const precioKey = `precio_${calidad}`;
    let precioUnitario = trabajo[precioKey] ?? trabajo.precio_std ?? 0;
    if (tipoPresupuesto === "mo") {
      precioUnitario = precioUnitario * 0.6;
    }
    return {
      trabajo_id: trabajo.id,
      trabajo_nombre: trabajo.nombre,
      cantidad: 1,
      unidad: trabajo.unidad,
      precio_unitario: precioUnitario,
      subtotal: precioUnitario,
      calidad,
      incluye_material: tipoPresupuesto === "mo_material",
      precio_material: 0,
      tiempo_estimado: Math.round((trabajo.tiempo_base_horas || 0.5) * 100) / 100,
    };
  };

  const enviar = async () => {
    const texto = input.trim();
    if (!texto || loading) return;

    const nuevosMensajes = [...mensajes, { role: "user", content: texto, imagen: imagenAdjunta }];
    setMensajes(nuevosMensajes);
    setInput("");
    const imagenEnviada = imagenAdjunta;
    setImagenAdjunta(null);
    setLoading(true);

    try {
      const historial = nuevosMensajes
        .filter((m) => m.role === "user" || m.role === "assistant")
        .slice(0, -1)
        .map((m) => ({ role: m.role, content: m.content }));

      const res = await api.post("/interpretar-pedido", {
        mensaje: texto,
        imagen_base64: imagenEnviada,
        historial,
      });

      if (res.trabajo_nombre) {
        const trabajo = trabajos.find((t) => t.nombre === res.trabajo_nombre);
        setMensajes([
          ...nuevosMensajes,
          {
            role: "assistant",
            content: res.explicacion || `Encontré esto: ${res.trabajo_nombre}`,
            propuesta: trabajo || null,
          },
        ]);
      } else if (res.necesita_aclaracion && res.pregunta_aclaratoria) {
        setMensajes([...nuevosMensajes, { role: "assistant", content: res.pregunta_aclaratoria }]);
      } else {
        setMensajes([
          ...nuevosMensajes,
          {
            role: "assistant",
            content: res.explicacion || "Eso no lo tenemos en el catálogo automático — dejame tus datos y Andy te confirma el precio a mano.",
          },
        ]);
      }
    } catch (e) {
      setMensajes([...nuevosMensajes, { role: "assistant", content: "Uy, tuve un problema técnico. Probá de nuevo en un rato." }]);
    } finally {
      setLoading(false);
    }
  };

  const confirmarAgregar = (idx, trabajo) => {
    onAgregarLinea(construirLinea(trabajo));
    setMensajes((prev) =>
      prev.map((m, i) => (i === idx ? { ...m, propuesta: null, agregado: true } : m))
    );
  };

  const descartarPropuesta = (idx) => {
    setMensajes((prev) => prev.map((m, i) => (i === idx ? { ...m, propuesta: null } : m)));
  };

  return (
    <>
      {/* Botón flotante */}
      <button
        onClick={() => setAbierto(!abierto)}
        aria-label="Asistente del presupuesto"
        style={{
          position: "fixed",
          right: 16,
          bottom: 88,
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "var(--brand-accent, #C9A24B)",
          color: "white",
          border: "none",
          boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 60,
          cursor: "pointer",
        }}
      >
        {abierto ? <X size={26} /> : <ChatCircleText size={26} weight="fill" />}
      </button>

      {abierto && (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 152,
            width: "min(360px, calc(100vw - 32px))",
            height: "min(480px, calc(100vh - 220px))",
            background: "var(--surface-primary, white)",
            borderRadius: 16,
            boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            zIndex: 60,
          }}
        >
          <div style={{ padding: "12px 16px", background: "var(--brand-primary, #1a3a4a)", color: "white", display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkle size={18} weight="fill" />
            <div className="font-bold text-small">Asistente del presupuesto</div>
          </div>

          {lineas && lineas.length > 0 && (
            <div style={{ borderBottom: "1px solid var(--border-subtle, #e5e5e5)" }}>
              <button
                onClick={() => setCarritoAbierto(!carritoAbierto)}
                style={{
                  width: "100%", padding: "10px 16px", background: "var(--surface-secondary, #f7f4ee)",
                  border: "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer",
                }}
              >
                <span className="text-tiny font-bold" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <ShoppingCart size={16} weight="fill" />
                  {lineas.length} {lineas.length === 1 ? "trabajo" : "trabajos"} en tu presupuesto · {formatCurrency(lineas.reduce((s, l) => s + (l.subtotal || 0), 0))}
                </span>
                {carritoAbierto ? <CaretUp size={14} /> : <CaretDown size={14} />}
              </button>
              {carritoAbierto && (
                <div style={{ padding: "4px 16px 10px", background: "var(--surface-secondary, #f7f4ee)" }}>
                  {lineas.map((l, i) => (
                    <div key={i} className="text-tiny" style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                      <span style={{ opacity: 0.85 }}>{l.trabajo_nombre}</span>
                      <span style={{ fontWeight: 600, whiteSpace: "nowrap", marginLeft: 8 }}>{formatCurrency(l.subtotal)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {lineas && lineas.length > 0 && (!cliente?.nombre?.trim() || !cliente?.telefono?.trim()) && (
            <div
              className="text-tiny"
              style={{
                padding: "8px 16px", background: "#FFF7E6", color: "#8a5a00",
                display: "flex", alignItems: "flex-start", gap: 6, borderBottom: "1px solid var(--border-subtle, #e5e5e5)",
              }}
            >
              <WarningCircle size={16} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />
              <span>Todavía falta tu nombre y teléfono (paso 1) para poder guardar el presupuesto.</span>
            </div>
          )}

          <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {mensajes.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "85%" }}>
                <div
                  className="text-small"
                  style={{
                    background: m.role === "user" ? "var(--brand-accent, #C9A24B)" : "var(--surface-secondary, #f0f0f0)",
                    color: m.role === "user" ? "white" : "inherit",
                    padding: "8px 12px",
                    borderRadius: 12,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {m.imagen && (
                    <img src={m.imagen} alt="Adjunta" style={{ width: "100%", borderRadius: 8, marginBottom: 6 }} />
                  )}
                  {m.content}
                </div>
                {m.propuesta && (
                  <div className="card p-3 mt-2" style={{ background: "var(--surface-secondary, #f0f0f0)" }}>
                    <div className="text-tiny font-bold mb-2">
                      {m.propuesta.nombre} — {(m.propuesta[`precio_${calidad}`] ?? m.propuesta.precio_std)}€ / {m.propuesta.unidad}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => confirmarAgregar(i, m.propuesta)}
                        className="btn btn-accent btn-sm"
                        style={{ display: "flex", alignItems: "center", gap: 4 }}
                      >
                        <Check size={14} /> Sí, agregar
                      </button>
                      <button onClick={() => descartarPropuesta(i)} className="btn btn-ghost btn-sm">
                        No, gracias
                      </button>
                    </div>
                  </div>
                )}
                {m.agregado && (
                  <div className="text-tiny mt-1" style={{ color: "var(--success, #2e7d32)" }}>✅ Agregado a tu presupuesto</div>
                )}
              </div>
            ))}
            {loading && <div className="text-tiny text-secondary">Pensando...</div>}
            <div ref={finRef} />
          </div>

          {imagenAdjunta && (
            <div style={{ padding: "0 12px" }}>
              <img src={imagenAdjunta} alt="A adjuntar" style={{ height: 50, borderRadius: 8 }} />
            </div>
          )}

          <div style={{ padding: 10, borderTop: "1px solid var(--border-subtle, #e5e5e5)", display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="file"
              accept="image/*"
              onChange={adjuntarFoto}
              style={{ display: "none" }}
              id="asistente-foto-input"
            />
            <label htmlFor="asistente-foto-input" style={{ cursor: "pointer", display: "flex" }}>
              <Camera size={22} />
            </label>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && enviar()}
              placeholder="Escribí lo que necesitás..."
              style={{ flex: 1, border: "none", outline: "none", background: "transparent" }}
              className="text-small"
            />
            <button onClick={enviar} disabled={loading || !input.trim()} style={{ border: "none", background: "transparent", cursor: "pointer", opacity: loading || !input.trim() ? 0.4 : 1 }}>
              <PaperPlaneRight size={22} weight="fill" style={{ color: "var(--brand-accent, #C9A24B)" }} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
